import { useState, useEffect } from "react";
import { useAuth, API } from "../../App";
import axios from "axios";
import { toast } from "sonner";
import { Globe, TrendingUp, Loader2 } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";

// Helper function for country flags
const getCountryFlag = (country) => {
  const flags = {
    "United Kingdom": "🇬🇧",
    "UK": "🇬🇧",
    "England": "🏴󠁧󠁢󠁥󠁮󠁧󠁿",
    "United States": "🇺🇸",
    "USA": "🇺🇸",
    "Germany": "🇩🇪",
    "France": "🇫🇷",
    "Spain": "🇪🇸",
    "Italy": "🇮🇹",
    "Ireland": "🇮🇪",
    "Australia": "🇦🇺",
    "Canada": "🇨🇦",
    "Netherlands": "🇳🇱",
    "Belgium": "🇧🇪",
    "Sweden": "🇸🇪",
    "Norway": "🇳🇴",
    "Denmark": "🇩🇰",
    "Local": "🏠",
    "Unknown": "🌍",
  };
  return flags[country] || "🌍";
};

const AnalyticsAdmin = () => {
  const { token } = useAuth();
  const [summary, setSummary] = useState(null);
  const [signupData, setSignupData] = useState([]);
  const [pageVisits, setPageVisits] = useState([]);
  const [users, setUsers] = useState({ users: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const [summaryRes, signupsRes, pagesRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/analytics/summary`, { headers: { Authorization: `Bearer ${token}` }}),
        axios.get(`${API}/admin/analytics/signups?days=30`, { headers: { Authorization: `Bearer ${token}` }}),
        axios.get(`${API}/admin/analytics/page-visits?days=30`, { headers: { Authorization: `Bearer ${token}` }}),
        axios.get(`${API}/admin/analytics/users?limit=100`, { headers: { Authorization: `Bearer ${token}` }})
      ]);
      
      setSummary(summaryRes.data);
      setSignupData(signupsRes.data);
      setPageVisits(pagesRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      console.error("Failed to fetch analytics:", error);
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#7C2C3B]" /></div>;
  }

  // Simple bar chart for signups
  const maxSignups = Math.max(...signupData.map(d => d.signups), 1);

  return (
    <div data-testid="admin-analytics">
      <h1 className="text-3xl font-bold text-[#0F172A] mb-6" style={{ fontFamily: 'Barlow Condensed' }}>
        <TrendingUp className="w-8 h-8 inline mr-2 text-[#2BAEE5]" />
        ANALYTICS & TRACKING
      </h1>
      
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-xl p-6 border border-slate-200">
          <div className="text-3xl font-bold text-[#0F172A]">{summary?.total_users || 0}</div>
          <div className="text-sm text-slate-500">Total Users</div>
        </div>
        <div className="bg-white rounded-xl p-6 border border-slate-200">
          <div className="text-3xl font-bold text-green-600">{summary?.subscribers || 0}</div>
          <div className="text-sm text-slate-500">Subscribers</div>
        </div>
        <div className="bg-white rounded-xl p-6 border border-slate-200">
          <div className="text-3xl font-bold text-[#2BAEE5]">{summary?.today_signups || 0}</div>
          <div className="text-sm text-slate-500">Signups Today</div>
        </div>
        <div className="bg-white rounded-xl p-6 border border-slate-200">
          <div className="text-3xl font-bold text-[#7C2C3B]">{summary?.today_logins || 0}</div>
          <div className="text-sm text-slate-500">Logins Today</div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="mb-6">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="users">User List</TabsTrigger>
          <TabsTrigger value="pages">Page Visits</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* Signup Graph */}
          <div className="bg-white rounded-xl p-6 border border-slate-200">
            <h2 className="text-xl font-bold text-[#0F172A] mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
              DAILY SIGNUPS (Last 30 Days)
            </h2>
            <div className="flex items-end gap-1 h-40 overflow-x-auto pb-2">
              {signupData.slice(-30).map((day, idx) => (
                <div key={idx} className="flex flex-col items-center min-w-[20px]">
                  <div 
                    className="w-4 bg-[#2BAEE5] rounded-t hover:bg-[#7C2C3B] transition-colors cursor-pointer"
                    style={{ height: `${Math.max((day.signups / maxSignups) * 120, 4)}px` }}
                    title={`${day.date}: ${day.signups} signups`}
                  />
                  {idx % 5 === 0 && (
                    <span className="text-[8px] text-slate-400 mt-1 rotate-45 whitespace-nowrap">
                      {day.date.slice(5)}
                    </span>
                  )}
                </div>
              ))}
            </div>
            <div className="text-xs text-slate-500 mt-2 text-center">
              Total signups in period: {signupData.reduce((sum, d) => sum + d.signups, 0)}
            </div>
          </div>

          {/* Top Countries */}
          <div className="bg-white rounded-xl p-6 border border-slate-200">
            <h2 className="text-xl font-bold text-[#0F172A] mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
              <Globe className="w-5 h-5 inline mr-2 text-[#2BAEE5]" />
              TOP COUNTRIES
            </h2>
            {summary?.top_countries?.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {summary.top_countries.map((country, idx) => (
                  <div key={idx} className="flex items-center gap-2 bg-slate-50 p-3 rounded-lg">
                    <span className="text-2xl">{getCountryFlag(country.country)}</span>
                    <div>
                      <div className="font-medium text-sm">{country.country}</div>
                      <div className="text-xs text-slate-500">{country.count} users</div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500">No location data yet. Users will be tracked on their next login.</p>
            )}
          </div>
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users">
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="p-4 border-b border-slate-200 bg-slate-50">
              <h2 className="font-bold text-[#0F172A]">User Details with IP & Location</h2>
              <p className="text-sm text-slate-500">Showing {users.users?.length || 0} of {users.total} users</p>
            </div>
            <div className="max-h-[500px] overflow-auto">
              <Table>
                <TableHeader className="sticky top-0 bg-white">
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Country</TableHead>
                    <TableHead>IP Address</TableHead>
                    <TableHead>Registered</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.users?.map(user => (
                    <TableRow key={user.id}>
                      <TableCell className="font-medium">{user.full_name}</TableCell>
                      <TableCell className="text-sm">{user.email}</TableCell>
                      <TableCell>
                        <span className="flex items-center gap-1">
                          {getCountryFlag(user.last_country || "Unknown")}
                          <span className="text-sm">{user.last_country || "Unknown"}</span>
                        </span>
                      </TableCell>
                      <TableCell className="font-mono text-xs">{user.last_ip || "N/A"}</TableCell>
                      <TableCell className="text-xs">{new Date(user.created_at).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {user.is_subscribed && (
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">Subscribed</span>
                          )}
                          {user.email_verified && (
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">Verified</span>
                          )}
                          {!user.is_subscribed && !user.email_verified && (
                            <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">Free</span>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </TabsContent>

        {/* Pages Tab */}
        <TabsContent value="pages">
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="p-4 border-b border-slate-200 bg-slate-50">
              <h2 className="font-bold text-[#0F172A]">Most Visited Pages (Last 30 Days)</h2>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">#</TableHead>
                  <TableHead>Page</TableHead>
                  <TableHead className="text-right">Visits</TableHead>
                  <TableHead className="text-right">Days Active</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pageVisits.length > 0 ? pageVisits.map((page, idx) => (
                  <TableRow key={idx}>
                    <TableCell className="font-bold text-slate-400">{idx + 1}</TableCell>
                    <TableCell className="font-medium">{page.page}</TableCell>
                    <TableCell className="text-right font-bold text-[#7C2C3B]">{page.visits}</TableCell>
                    <TableCell className="text-right text-slate-500">{page.days_active}</TableCell>
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-slate-500 py-8">
                      No page visit data yet. Page tracking will begin when users navigate the site.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AnalyticsAdmin;
